/**
 * Live badge count via initial PostgREST fetch + Realtime postgres_changes
 * subscription. Day 63b promotion of the Day 62b inline pattern at
 * Sidebar.tsx:60+L154 — rule-of-three trigger fires with the second
 * consumer (Error Log Viewer's open-fingerprints badge alongside the
 * existing Feedback Inbox open-feedback badge).
 *
 * V1 supports `eq`-only filters via the shorthand `Record<string, string>`
 * shape. Compound filters AND-join via PostgREST `.eq()` chain. When a
 * future consumer needs `gt` / `like` / etc., extend with explicit
 * `{ status: { op: 'eq', value: 'open' } }` syntax — defer until first
 * non-eq consumer surfaces.
 *
 * Realtime channel listens to ALL changes on the table (no server-side
 * filter) and refetches via PostgREST chain when any change arrives.
 * Mirrors the Day 62b inline pattern's behavior — slightly noisier than
 * server-side filtered subscription but correct: any change to the table
 * could affect the count, and the refetch reapplies all filter keys.
 *
 * Two-callback `.then(success, error)` hardening preserves the inline
 * pattern's auth-latency-tolerant behavior. Cancellation guard prevents
 * stale setState after unmount.
 *
 * Day 64 candidate consumers: Backlog Manager (open backlog items),
 * User Management (pending invites), Build Tracker (recent failures),
 * Algopix Job Monitor (running jobs), Release Notes Manager
 * (unpublished drafts) — five additional /admin/* surfaces will likely
 * adopt this hook as their nav badges land.
 */

import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface BadgeCountSpec {
  table: string;
  filter: Record<string, string>;
  channelName: string;
}

export function useBadgeCount(spec: BadgeCountSpec): number {
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;

    const refetch = (): void => {
      // The hook is intentionally generic over table name. Supabase's
      // typed `from` method only accepts known-literal table names from
      // the generated Database type union; cast to `never` per the LooseRpc
      // pattern (CLAUDE_REFERENCE.md §6) to allow runtime table selection.
      let query = (supabase.from as unknown as (t: string) => ReturnType<typeof supabase.from>)(
        spec.table
      ).select('id', { count: 'exact', head: true });
      for (const [k, v] of Object.entries(spec.filter)) {
        query = query.eq(k, v);
      }
      query.then(
        ({ count: c, error }) => {
          if (cancelled) return;
          if (error) {
            console.warn(`[useBadgeCount/${spec.table}] count fetch failed`, error.message);
            return;
          }
          setCount(c ?? 0);
        },
        (err: Error) => {
          if (cancelled) return;
          console.warn(`[useBadgeCount/${spec.table}] count fetch threw`, err.message);
        }
      );
    };

    refetch();

    const channel = supabase
      .channel(spec.channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: spec.table },
        refetch
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
    // The filter object reference changes per render in callers that pass
    // an object literal; Object.entries is stable per shallow content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.table, spec.channelName, JSON.stringify(spec.filter)]);

  return count;
}
